import { useEffect, useState } from 'react';
import { Button, Dialog, Field, Icon, Input, useErrorDialog, useToast } from '@dayon/design-system';

import { api, ApiRequestError } from '../../lib/api.ts';

/**
 * Rewrite a draft's copy — the middle step that had no route.
 *
 * `POST /api/platform/announcements/:id/schedule` has always refused a draft
 * missing a locale, and told the operator to supply it. Nothing could write
 * copy, so the refusal named a remedy the platform did not have: the drawer
 * said so in as many words ("There is also no route that WRITES copy, so the
 * missing locale cannot be filled in from here"). `PATCH /api/platform/
 * announcements/:id` closes that.
 *
 * IT IS A MERGE, not a replacement, and the field left blank is left ALONE.
 * The commonest edit here is supplying the one locale that is blocking a
 * schedule, and a PATCH carrying only Filipino must not silently drop the
 * English that was already written.
 *
 * COPY ONLY. Kind, channels, audience and brand are not editable: changing who
 * an announcement goes to is composing a different announcement, and the route
 * refuses anything that is not a DRAFT.
 */
export interface DraftCopy {
  id: string;
  ref: string;
  status: string;
  english: string | null;
  filipino: string | null;
}

export function RewriteCopyDialog({
  draft,
  onClose,
  onDone,
}: {
  draft: DraftCopy | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [english, setEnglish] = useState('');
  const [filipino, setFilipino] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEnglish(draft?.english ?? '');
    setFilipino(draft?.filipino ?? '');
    setFields({});
  }, [draft]);

  const close = () => {
    setFields({});
    onClose();
  };

  const submit = async () => {
    if (!draft) return;
    /* Only what changed, and never an empty string: the route treats a locale
       carrying no text as no copy at all, and sending '' would be asking it to
       record a blank title. Clearing a locale is not something this form
       offers — an announcement with its English deleted is a worse state than
       one that never had it. */
    const copy: Record<string, string> = {};
    if (english.trim() && english.trim() !== (draft.english ?? '')) copy['en-PH'] = english.trim();
    if (filipino.trim() && filipino.trim() !== (draft.filipino ?? '')) {
      copy['fil-PH'] = filipino.trim();
    }
    if (Object.keys(copy).length === 0) {
      setFields({ copy: 'Nothing has been changed yet.' });
      return;
    }

    setBusy(true);
    try {
      const result = await api.patch<{ ref: string; missingLocales: string[] }>(
        `/api/platform/announcements/${draft.id}`,
        { copy },
      );
      toast(
        result.missingLocales.length === 0
          ? `${result.ref} now has both locales and can be scheduled.`
          : `${result.ref} updated — still missing ${result.missingLocales.join(' and ')}.`,
      );
      onDone();
      close();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'Could not rewrite the copy',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={draft !== null}
      onClose={close}
      wide
      title={draft ? `Rewrite the copy on ${draft.ref}` : 'Rewrite copy'}
      actions={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={() => void submit()}>
            Save the copy
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="notice notice-quiet">
          <Icon name="translate" />
          <div>
            Both locales are required before this can be scheduled. A locale left blank here is left
            as it is — this saves what changed and never clears the other one.
          </div>
        </div>

        <Field label="Title shown to players · English (PH)" error={fields['copy']}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={english}
              onChange={(e) => setEnglish(e.target.value)}
              placeholder="September reload 50% — opt in now"
            />
          )}
        </Field>

        <Field label="Title shown to players · Filipino">
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={filipino}
              onChange={(e) => setFilipino(e.target.value)}
              placeholder="Setyembre reload 50% — mag-opt in na"
            />
          )}
        </Field>

        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            Only a <strong>draft</strong> can be rewritten. Once a send is queued the copy is fixed
            — cancel the send first, which returns it to Draft with its audience and channels
            intact. A sent announcement can never be edited: the players who received it already
            have it.
          </div>
        </div>
      </div>
    </Dialog>
  );
}
